import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import "./ooraTomtomTraffic.css";
import { viewOoraTomtomTraffic } from "../../Redux/Actions/ooraTrafficTomActions";
import {
  formatDate, formatDateTime, formatSlot, formatDuration, formatDistance,
  kindOf, KIND_LABEL, speedText, speedLevel, routeText,
} from "./ooraTomtomTrafficHelpers";
import OoraTomtomRouteMap from "./ooraTomtomRouteMap";

const ViewOoraTomtomTraffic = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { ooraTomtomTraffic: record, singleError: viewError } = useSelector((s) => s.ooraTomtomTraffic || {});

  const data = record?.data;
  const ready = data?._id === id; // ignore a record left over from another id

  useEffect(() => {
    dispatch(viewOoraTomtomTraffic(id));
  }, [dispatch, id]);

  const isIncident = ready && kindOf(data) === "INCIDENT";
  const jams = (ready && data.jammed_roads) || [];
  const ratio =
    ready && data.static_duration_sec && data.traffic_duration_sec
      ? (data.traffic_duration_sec / data.static_duration_sec).toFixed(2)
      : "-";

  return (
    <div className="ott-page">
      <div className="ott-head">
        <div>
          <h1>{ready ? data.road_name : "TomTom Traffic Details"}</h1>
          <p>{ready ? routeText(data) : "One saved TomTom reading"}</p>
        </div>
        <button type="button" className="ott-btn" onClick={() => navigate("/oora_tomtom_traffic")}>Back</button>
      </div>

      <div className="ott-panel">
        {!ready ? (
          <p className="ott-empty" style={{ margin: 0 }}>
            {viewError ? "Traffic record not found" : "Loading..."}
          </p>
        ) : (
          <div className="ott-body">
            <dl className="ott-facts">
              <div><dt>Date</dt><dd>{formatDate(data.date_label)}</dd></div>
              <div><dt>Slot (IST)</dt><dd>{formatSlot(data.slot)}</dd></div>
              <div><dt>Kind</dt><dd>{KIND_LABEL[kindOf(data)]}</dd></div>
              <div><dt>Last updated</dt><dd>{formatDateTime(data.updated_at)}</dd></div>
              <div>
                <dt>From</dt>
                <dd>{data.from?.name || "-"}<small>{data.from?.lat}, {data.from?.lng}</small></dd>
              </div>
              <div>
                <dt>To</dt>
                <dd>{data.to?.name || "-"}<small>{data.to?.lat}, {data.to?.lng}</small></dd>
              </div>
              <div><dt>{isIncident ? "Jam length" : "Route distance"}</dt><dd>{formatDistance(data.distance_meters)}</dd></div>
              <div><dt>Route via</dt><dd>{data.route_via || "-"}</dd></div>
            </dl>

            <div className="ott-section">
              <h3>Map</h3>
              <span className="ott-note">
                <span className="ott-key ott-key--jam" />Jam
                <span className="ott-key ott-key--slow" />Slow
              </span>
            </div>
            <OoraTomtomRouteMap path={data.path || []} jams={jams} wholeIsJam={isIncident} />

            <div className="ott-section">
              <h3>Traffic</h3>
              <span className={`ott-badge ott-lvl-${data.traffic_level}`}>{data.traffic_level}</span>
            </div>
            <div className="ott-box ott-scroll">
              <table className="ott-table">
                <thead>
                  <tr>
                    <th>Travel time</th>
                    <th>Normal time</th>
                    <th>Delay</th>
                    <th>Ratio</th>
                    {!isIncident && <th>Readings</th>}
                    {!isIncident && <th>Avg travel time</th>}
                    {!isIncident && <th>Avg delay</th>}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{formatDuration(data.traffic_duration_sec)}</td>
                    <td>{formatDuration(data.static_duration_sec)}</td>
                    <td>{formatDuration(data.delay_sec)}</td>
                    <td className="ott-num">{ratio}</td>
                    {!isIncident && <td className="ott-num">{data.readings ?? "-"}</td>}
                    {!isIncident && <td>{formatDuration(data.avg_traffic_duration_sec)}</td>}
                    {!isIncident && <td>{formatDuration(data.avg_delay_sec)}</td>}
                  </tr>
                </tbody>
              </table>
            </div>
            {isIncident && (
              <p className="ott-note">TomTom reports incidents as a jam only, so travel time and ratio are not available.</p>
            )}

            <div className="ott-section">
              <h3>Jammed roads</h3>
              <span className="ott-note">{jams.length} road{jams.length === 1 ? "" : "s"}</span>
            </div>
            {jams.length ? (
              <div className="ott-box ott-scroll">
                <table className="ott-table">
                  <thead>
                    <tr>
                      <th>Road</th>
                      <th>Type</th>
                      <th>Jam length</th>
                      <th>Start (lat, lng)</th>
                      <th>End (lat, lng)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jams.map((j, i) => (
                      <tr key={`${j.road_place_id}-${i}`}>
                        <td className="ott-strong">{j.road_name}</td>
                        <td><span className={`ott-badge ott-lvl-${speedLevel(j.speed)}`}>{speedText(j.speed)}</span></td>
                        <td>{formatDistance(j.jam_length_meters)}</td>
                        <td className="ott-num">{j.start?.lat}, {j.start?.lng}</td>
                        <td className="ott-num">{j.end?.lat}, {j.end?.lng}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="ott-note">No jam on this road in this slot.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewOoraTomtomTraffic;